import React, { useContext, useEffect, useMemo } from "react";
import { Controller, useForm } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router-dom";

import { Button } from "primereact/button";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { InputMask } from "primereact/inputmask";
import { Password } from "primereact/password";

import EntityFormPage from "../../../../components/EntityFormPage/EntityFormPage";
import { ToastContext } from "../../../../App";
import { useCreateUserMutation } from "../../../../redux/services/user";
import { useGetOrganizationListQuery } from "../../../../redux/services/organization";

import * as styles from "../UserManagement.module.css";

const LIST_PATH = "/app/admin/users";

function UserCreatePage() {
  const showToast = useContext(ToastContext);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const orgSubdomain = searchParams.get("org");

  const { data: organizations } = useGetOrganizationListQuery();

  const organization = useMemo(
    () => organizations?.find((o) => o.subdomain === orgSubdomain),
    [organizations, orgSubdomain],
  );

  const { control, handleSubmit } = useForm({
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      password: "",
      role: "",
    },
  });

  const [
    createUser,
    {
      isSuccess: isCreateUserSuccess,
      isError: isCreateUserError,
      error: createUserError,
    },
  ] = useCreateUserMutation();

  useEffect(() => {
    if (isCreateUserSuccess) {
      showToast("success", "User", "User created!");
      navigate(LIST_PATH);
    }
  }, [isCreateUserSuccess]);

  useEffect(() => {
    if (isCreateUserError) {
      showToast(
        "error",
        createUserError?.status,
        createUserError?.data?.message ||
          (createUserError?.data?.errors && "Incorrect data"),
      );
    }
  }, [isCreateUserError]);

  const onCreate = (data) => {
    if (!organization) {
      showToast("warn", "Warning", "Please select a organization first");
      return;
    }
    const { name, email, password, phone, role } = data;
    createUser({
      subdomain: organization.subdomain,
      payload: {
        name,
        email,
        password,
        phone: `+1${phone}`,
        userRole: role,
      },
    });
  };

  const title = organization
    ? `New User — ${organization.name}`
    : "New User";

  return (
    <EntityFormPage
      title={title}
      backTo={LIST_PATH}
      footer={
        <Button
          label="Save"
          type="submit"
          className={`p-button-success ${styles.saveBtn}`}
          onClick={handleSubmit(onCreate)}
        />
      }
    >
      <form onSubmit={handleSubmit(onCreate)}>
        <Controller
          name="name"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Name</label>
              <InputText
                className={`w-full mb-3 ${styles.input}`}
                placeholder="Name"
                {...field}
              />
            </div>
          )}
        />

        <Controller
          name="role"
          control={control}
          render={({ field }) => (
            <div className={`field ${styles.dropDownWrapper}`}>
              <label>Role</label>
              <Dropdown
                id={field.name}
                value={field.value}
                onChange={(e) => field.onChange(e.value)}
                placeholder="Select role"
                className="w-full mb-3"
                panelClassName={styles.dropDownPanel}
                options={[
                  { label: "Admin", value: "admin" },
                  { label: "Manager", value: "manager" },
                  { label: "Chef", value: "chef" },
                ]}
              />
            </div>
          )}
        />

        <Controller
          name="email"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Email</label>
              <InputText
                className={`w-full mb-3 ${styles.input}`}
                placeholder="Email"
                {...field}
              />
            </div>
          )}
        />

        <Controller
          name="phone"
          control={control}
          rules={{
            required: "Enter your phone number",
            minLength: { value: 10, message: "Phone number is invalid" },
          }}
          render={({ field, fieldState }) => (
            <div className="field">
              <label>Phone Number</label>
              <div>
                <div className="p-inputgroup flex-1">
                  <span
                    className={`p-inputgroup-addon mb-3 ${styles.phonePrefix}`}
                  >
                    +1
                  </span>
                  <InputMask
                    className={`w-full mb-3 ${styles.input} ${styles.inputPhone} ${
                      fieldState.invalid ? "p-invalid" : ""
                    }`}
                    placeholder="(999) 999-9999"
                    prefix="+1"
                    mask="(999) 999-9999"
                    unmask={true}
                    {...field}
                  />
                </div>
                {fieldState.error && (
                  <small className="p-error">{fieldState.error.message}</small>
                )}
              </div>
            </div>
          )}
        />

        <Controller
          name="password"
          control={control}
          render={({ field }) => (
            <div className="field">
              <label>Password</label>
              <Password
                className={`w-full ${styles.passwordInput}`}
                autoComplete="new-password"
                type="password"
                placeholder="Password"
                toggleMask
                feedback={false}
                {...field}
              />
            </div>
          )}
        />

        <button type="submit" hidden />
      </form>
    </EntityFormPage>
  );
}

export default UserCreatePage;
